/**
 * @file src/pages/IssueForm.tsx
 * @fileoverview Definieert het IssueForm component, een modaal formulier waarmee gebruikers
 * problemen, vragen of suggesties kunnen indienen, die vervolgens als issue in GitHub worden aangemaakt.
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useVersionInfo } from '../hooks/useVersionInfo';
import { useUserStore } from '../store/userStore';
import { useI18n } from '../i18n';
import usePageView from '../hooks/usePageView';
import { AppIcons } from '../config/AppIcons';
import { getBackendVersionApi, getBackendVersionValue } from '../lib/versionApi';
import { getAuthenticatedSiteUrl, getSiteUrl } from '../utils/siteUrl';
import { authenticatedFetch, getAuthHeaders } from '../lib/authApi';

/**
 * Props voor de IssueForm component.
 */
interface IssueFormProps {
  /** Callback functie die wordt aangeroepen wanneer het formulier wordt gesloten. */
  onClose: () => void;
  /** Geeft aan of het formulier momenteel open is. */
  isOpen: boolean;
}

const buildEnglishIssueTemplate = (type: 'enhancement' | 'question' | 'bug', description: string) => {
  const details = description.trim() || '<!-- Describe the reason for this issue. -->';
  const mainSections = type === 'bug'
    ? `## Current behaviour\n\n${details}\n\n## Steps to reproduce\n\n<!-- Add clear reproduction steps. -->\n\n## Expected behaviour\n\n<!-- Describe what should happen instead. -->`
    : `## Context\n\n${details}\n\n## Expected outcome\n\n<!-- Describe the desired user-facing result. -->`;

  return `${mainSections}\n\n## Scope\n\n<!-- List the required change, relevant environment and constraints. -->\n\n## Acceptance criteria\n\n- [ ] Expected behaviour works\n- [ ] Relevant tests pass\n- [ ] Desktop and mobile are verified where applicable\n- [ ] Documentation is updated where needed`;
};

/**
 * IssueForm is een modaal venster waarmee gebruikers feedback kunnen geven.
 * Het stelt gebruikers in staat om een titel, beschrijving en label (bug, vraag, verbetering)
 * voor hun feedback op te geven. Bij het indienen wordt een issue aangemaakt in een GitHub repository
 * via een API-aanroep.
 *
 * @param {IssueFormProps} props - De props voor de component.
 */
const IssueForm: React.FC<IssueFormProps> = ({ onClose, isOpen }) => {
  const { currentUser } = useUserStore();
  const { t } = useI18n();
  const [aiVersion, setAiVersion] = useState('');
  const versionInfo = useVersionInfo();
  usePageView('issue_form', { page: 'issue_form' });

  useEffect(() => {
    console.log('IssueForm mounted with isOpen:', isOpen);
    return () => {
      console.log('IssueForm unmounted');
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchAiVersion = async () => {
      try {
        const data = await getBackendVersionApi();
        setAiVersion(getBackendVersionValue(data));
      } catch (error) {
        console.error('Failed to fetch AI version:', error);
      }
    };

    void fetchAiVersion();
  }, []);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [label, setLabel] = useState<'enhancement' | 'question' | 'bug'>('enhancement');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
      setError(t('issue_form_error_required'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const apiUrl = import.meta.env.VITE_AI_API_URL;
      const response = await authenticatedFetch(`${apiUrl}/github/issue`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          title,
          body: `${buildEnglishIssueTemplate(label, body)}\n\n---\n### doHockey user\n- name: ${currentUser?.displayName ?? currentUser?.username ?? ''}\n- email: ${currentUser?.email ?? ''}\n\nPlatform version: ${versionInfo?.version ?? ''} build ${versionInfo?.buildNumber ?? ''}\nAPI version: ${aiVersion}`,
          assignees: ['markmastop'],
          labels: [label],
          milestone: 'Feedback',
          email: currentUser?.email ?? undefined,
          language: currentUser?.language?.toLowerCase().startsWith('nl') ? 'nl' : 'en',
        }),
      });

      if (!response.ok) throw new Error('submit failed');

      const data = await response.json();
      
      // Add validation and better error handling for issue number extraction
      let issueNumber = '';
      if (data.issue_url) {
        const match = data.issue_url.match(/\/issues\/(\d+)/);
        if (match) {
          issueNumber = match[1];
          console.log('Extracted issue number:', issueNumber);
        } else {
          console.error('Failed to extract issue number from URL:', data.issue_url);
        }
      } else {
        console.error('No issue_url in response:', data);
      }

      setSuccess(t('issue_form_success', { number: issueNumber }));
      setTitle('');
      setBody('');
      setLabel('enhancement');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[9999] flex items-end justify-center bg-white min-[900px]:fixed min-[900px]:items-center min-[900px]:bg-black/50 min-[900px]:p-4">
      <div className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white min-[900px]:h-auto min-[900px]:max-h-[calc(100dvh-2rem)] min-[900px]:rounded-lg min-[900px]:shadow-xl">
        <div className="flex-shrink-0 border-b p-4 sm:p-6">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <h2 className="flex min-w-0 items-center text-lg font-semibold sm:text-xl">
                <AppIcons.support className="mr-2 text-primary-600" />
                {t('issue_form_title')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              aria-label={t('issue_form_close')}
            >
              <X size={24} />
            </button>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              <a
                href={getAuthenticatedSiteUrl('/support')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <AppIcons.support size={12} />
                {t('issue_form_link_support')}
              </a>
              <a
                href={getSiteUrl('/support/docs')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <AppIcons.knowledge_base size={12} />
                {t('issue_form_link_knowledge_base')}
              </a>
              <a
                href={getAuthenticatedSiteUrl('/support/forum')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <AppIcons.forum size={12} />
                {t('issue_form_link_forum')}
              </a>
          </div>
          <p className="mt-3 hidden text-sm text-gray-600 sm:block">{t('issue_form_intro')}</p>
        </div>
        <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          {success && (
            <div className="p-2 text-success-700 bg-success-50 border border-success-200 rounded text-sm">
              {success}
            </div>
          )}
          {error && (
            <div className="p-2 text-error-700 bg-error-50 border border-error-200 rounded text-sm">
              {error}
            </div>
          )}
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('issue_form_label_label')}</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setLabel('enhancement')}
                disabled={isSubmitting}
                className={`inline-flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-[11px] font-medium transition-colors sm:gap-2 sm:px-3 sm:text-sm ${
                  label === 'enhancement'
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <AppIcons.issue_label_enhancement size={14} />
                {t('issue_form_option_suggestion')}
              </button>
              <button
                type="button"
                onClick={() => setLabel('question')}
                disabled={isSubmitting}
                className={`inline-flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-[11px] font-medium transition-colors sm:gap-2 sm:px-3 sm:text-sm ${
                  label === 'question'
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <AppIcons.issue_label_question size={14} />
                {t('issue_form_option_question')}
              </button>
              <button
                type="button"
                onClick={() => setLabel('bug')}
                disabled={isSubmitting}
                className={`inline-flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-[11px] font-medium transition-colors sm:gap-2 sm:px-3 sm:text-sm ${
                  label === 'bug'
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <AppIcons.issue_label_bug size={14} />
                {t('issue_form_option_problem')}
              </button>
            </div>
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_form_title_label')}</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_form_body_label')}</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="sticky bottom-0 -mx-4 -mb-4 flex justify-end border-t border-gray-200 bg-white px-4 py-3 min-[900px]:static min-[900px]:m-0 min-[900px]:border-0 min-[900px]:bg-transparent min-[900px]:p-0">
            <button
              type="submit"
              className={`flex min-h-11 w-full items-center justify-center rounded-lg px-4 py-2 transition-colors sm:w-auto ${
                isSubmitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('issue_form_submitting') : t('issue_form_submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueForm;
